
import Validator from 'Src/js/utilities/validator';
import Vue from 'vue';
import { Component, Inject, Prop, Watch } from 'vue-property-decorator';
import { Get } from 'vuex-pathify';

import { FulfillmentType } from 'Src/js/types/ProductDelivery';
import { IBackInStockService, ResponseType } from '../../services/BackInStockService';
import { IProductBackInStockInfo } from 'Src/js/types/Product';

import DotLoader from 'Src/js/components/DotLoader.vue';
import FulfillmentOptionLink from './FulfillmentOptionLink.vue';
import Icon from 'Src/js/components/Icon.vue';
import TransitionExpand from 'Src/js/components/transitions/TransitionExpand.vue';
import UiButton from 'Src/js/components/UiButton.vue';
import VisuallyHidden from 'Src/js/components/VisuallyHidden.vue';
import { icons } from 'Src/js/constants/icons';

@Component({
	name: 'StockSubscriptionForm',
	components: {
		UiButton,
		Icon,
		FulfillmentOptionLink,
		VisuallyHidden,
		DotLoader,
		TransitionExpand,
	},
})
class StockSubscriptionForm extends Vue {
	@Prop({ type: String, required: true })
	blainNumber: string;

	@Prop({ type: String, default: 'any' })
	fulfillmentType: FulfillmentType | 'any';

	@Prop({ type: String })
	defaultEmailAddress?: string;

	@Prop({ type: String, default: 'Sign up to receive an email when this item is back in stock.' })
	formDescription: string;

	@Inject('BackInStockService')
	readonly _backInStockService: IBackInStockService;

	@Get('product.backInStockInfo')
	readonly backInStockInfo: IProductBackInStockInfo;

	emailAddress: string = '';
	shouldValidateInput: boolean = false;
	state: 'default' | 'subscribing' | 'error' | 'success' | 'unsubscribing' = 'default';
	errorMessage: string | null = null;
	formLiveMessage: string | null = null;

	$refs: {
		successTitle: HTMLDivElement | null;
	}

	get emailInputID(): string {
		return `stock-sub-email-${this.fulfillmentType}`;
	}

	get formDescriptionID(): string {
		return `stock-sub-form-description-${this.fulfillmentType}`;
	}

	get errorMessageID(): string {
		if (this.state === 'error' && this.errorMessage) {
			return `stock-sub-error-message-${this.fulfillmentType}`;
		}

		return null;
	}

	get emailInputIcon() {
		if (!this.shouldValidateInput) return;

		if (this.state === 'error' && this.errorMessage) {
			return {
				name: icons.alert,
				className: 'error',
			};
		} else if (this.emailAddress !== '') {
			return {
				name: icons.checkmark,
				className: 'valid',
			};
		}

		return null;
	}

	get isAlreadySubscribed() {
		return this.backInStockInfo.forFulfillmentType[this.fulfillmentType].isAlreadySubscribed;
	}
	set isAlreadySubscribed(subscribed: boolean) {
		this.$store.dispatch('updateAlreadySubscribed', { fulfillmentType: this.fulfillmentType, subscribed });
	}

	async submit() {
		if (this.state !== 'default' && this.state !== 'error') return;

		this.shouldValidateInput = true;

		const emailError = this.validateEmail();

		if (emailError) {
			this.setEmailError(emailError);
			return;
		}

		this.state = 'subscribing';
		this.errorMessage = null;


		try {
			const response = await this._backInStockService.subscribe(this.blainNumber, this.emailAddress, this.fulfillmentType);

			switch (response.type) {
				case ResponseType.Subscribed:
					this.errorMessage = null;
					this.shouldValidateInput = false;
					this.state = 'success';
					this.isAlreadySubscribed = true;

					this.$nextTick(() => {
						this.$refs.successTitle?.focus();
					});

					break;
				case ResponseType.InvalidEmail:
					this.setEmailError('Invalid email address');
					break;
				default:
					this.setEmailError('Something went wrong. Please try again.');
			}
		} catch (_e) {
			this.setEmailError('Something went wrong. Please try again.');
		}
	}

	async unsubscribe() {
		if (this.state !== 'success') return;

		this.state = 'unsubscribing';

		try {
			await this._backInStockService.unsubscribe(this.blainNumber, this.emailAddress, this.fulfillmentType);
			this.isAlreadySubscribed = false;
			this.formLiveMessage = "You have been unsubscribed from notifications";
		} catch (_e) {
			// What should happen if this request fails?
		}

		this.emailAddress = this.defaultEmailAddress ?? '';
		if (this.defaultEmailAddress) {
			this.startValidating();
		}
		this.state = 'default';
	}

	startValidating() {
		this.shouldValidateInput = true;

		const emailError = this.validateEmail();
		if (emailError) {
			this.setEmailError(emailError);
		}
	}

	setEmailError(error: string): void {
		this.state = 'error';
		this.errorMessage = error;
	}

	validateEmail(emailAddress: string = this.emailAddress): string | null {
		if (emailAddress === '') {
			return 'Email address required';
		}

		if (!Validator.validateEmail(emailAddress)) {
			return 'Invalid email address';
		}

		return null;
	}

	timeout: number | null = null;
	debouncedReValidation(emailAddress: string) {
		clearTimeout(this.timeout);

		// @ts-ignore
		this.timeout = setTimeout(() => {
			const emailError = this.validateEmail(emailAddress);
			if (emailError) {
				this.setEmailError(emailError);
			} else {
				this.state = 'default';
			}
		}, 100);
	}

	@Watch('emailAddress')
	onEmailAddressChange(emailAddress: string) {
		if (this.state === 'error' || this.shouldValidateInput) {
			this.debouncedReValidation(emailAddress);
		}
	}

	created() {
		if (this.isAlreadySubscribed) {
			this.state = 'success';
		}
	}

	mounted() {
		if (this.defaultEmailAddress) {
			this.emailAddress = this.defaultEmailAddress;
			if (!this.isAlreadySubscribed) {
				this.shouldValidateInput = true;
			}
		}
	}
}

export default StockSubscriptionForm;
